import React, { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Checkbox, FormControl, IconButton, InputAdornment, MenuItem } from '@mui/material';
import { isArray } from 'lodash';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';
import * as yup from 'yup';

import { systemRoles } from './UserControl';
import ControlledInput from '../../../components/forms/ControlledInput';
import ControlledSelect from '../../../components/forms/ControlledSelect';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import { OrganizationType } from '../../../hooks/useSchoolInfo';
import { type UsersRecord } from '../../../types/apiTypes';
import AdminSettingsContainer from '../AdminSettingsContainer';
import { Footer, ModalButton } from '../styles';

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
`;

const StyledFormControl = styled(FormControl)`
    margin-bottom: 1rem;

    &:nth-last-child(2) {
        margin-bottom: 2rem;
    }
`;

export type DefaultValues = Partial<UsersRecord> & {
    password?: string | null;
};

interface ZoomUserFormProps {
    onSubmit: (values: DefaultValues) => void;
    initialValues?: DefaultValues;
    handleModalClose: () => void;
}

const UserForm: React.FC<ZoomUserFormProps> = ({ onSubmit, initialValues, handleModalClose }) => {
    const { t } = useTranslation('admin_settings');

    const { school } = useContainer(AdminSettingsContainer);

    const { data: orgSettings } = useOrgSettings();

    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const minPasswordLength = 10;
    const schema = yup.object().shape(
        {
            user_name: yup.string().required(t('users.dialogs.error-username-required')),
            email: yup
                .string()
                .nullable()
                .notRequired()
                .email(t('users.dialogs.error-valid-email-required'))
                .when('ext_user_id', {
                    is: (ext_user_id: string | null) => !ext_user_id,
                    then: yup.string().required(t('users.dialogs.error-email-or-ext-user-id-required'))
                }),
            ext_user_id: yup
                .string()
                .nullable()
                .notRequired()
                .when('email', {
                    is: (email: string | null) => !email,
                    then: yup.string().required(t('users.dialogs.error-email-or-ext-user-id-required'))
                }),
            password: yup
                .string()
                .nullable()
                .transform((value, original) => (original === '' ? undefined : value))
                .notRequired()
                .min(minPasswordLength, t('users.dialogs.error-password-min', { minPasswordLength }))
                .matches(/.*[0-9].*/, t('users.dialogs.error-password-num'))
                .matches(/.*[a-zA-Z].*/, t('users.dialogs.error-password-alpha')),
            roles: yup.array().of(yup.string())
        },
        [['email', 'ext_user_id']]
    );

    const defaultValues: DefaultValues = initialValues ?? {
        email: '',
        user_name: '',
        ext_user_id: '',
        roles: []
    };

    const { handleSubmit, control, watch } = useForm({
        defaultValues,
        resolver: yupResolver(schema),
        shouldUnregister: true
    });

    const returnSelectedValues = (selected) => {
        if (isArray(selected)) {
            return selected
                .map((role) => {
                    if (systemRoles.includes(role)) {
                        return t(`users.userRoles.${role}`);
                    }

                    return role;
                })
                .join(', ');
        }

        return '';
    };

    const roles = watch('roles');

    return (
        <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl fullWidth>
                <ControlledInput
                    type="text"
                    name="user_name"
                    control={control}
                    label={t('users.username')}
                    helperText={t('users.dialogs.username-helper-text')}
                />
            </StyledFormControl>
            <StyledFormControl fullWidth>
                <ControlledInput
                    type="text"
                    name="email"
                    control={control}
                    label={t('users.email')}
                    helperText={t('users.dialogs.email-helper-text')}
                />
            </StyledFormControl>

            {orgSettings?.featureSettings.options.useIdentifierInRoster && (
                <StyledFormControl fullWidth>
                    <ControlledInput
                        type="text"
                        name="ext_user_id"
                        control={control}
                        label={t('users.ext-user-id')}
                        helperText={t('users.dialogs.ext-user-id-helper-text')}
                    />
                </StyledFormControl>
            )}

            <StyledFormControl fullWidth>
                <ControlledSelect
                    name="roles"
                    control={control}
                    labelId="select-user-role-label"
                    id="select-user-role"
                    multiple
                    defaultValue={[]}
                    renderValue={returnSelectedValues}
                    label={t('users.roles')}
                    helperText={t('users.dialogs.role-helper-text')}
                >
                    {systemRoles.map((role, index) => {
                        const hasRole = roles?.includes(role) ?? false;
                        const labelName = t(`users.userRoles.${role}`);

                        return (
                            <MenuItem key={index} value={role}>
                                <Checkbox aria-label={labelName} checked={hasRole} />
                                {labelName}
                            </MenuItem>
                        );
                    })}
                </ControlledSelect>
            </StyledFormControl>
            {school?.type !== OrganizationType.User ? (
                <StyledFormControl fullWidth>
                    <ControlledInput
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        control={control}
                        label={t('users.password')}
                        helperText={t('users.dialogs.password-helper-text-detailed')}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                    size="small"
                                    sx={{ marginRight: '2px' }}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                </StyledFormControl>
            ) : null}
            <Footer>
                <ModalButton onClick={handleModalClose} color="secondary" variant="outlined">
                    {t('users.actions.cancel')}
                </ModalButton>
                <ModalButton type="submit" disableElevation variant="contained">
                    {t('users.actions.save')}
                </ModalButton>
            </Footer>
        </StyledForm>
    );
};

export default UserForm;
